import { Component, Input, OnInit } from '@angular/core';
import { AbstractControl, FormArray, FormControl, FormGroup, FormGroupDirective, ValidatorFn, Validators } from '@angular/forms';
import { CalcValidators } from '@app/core/directives/validators/calc-validators';
import { MetabolicoService } from '@app/core/services/api';

interface CardiacProfileNode {
  key?: string;
  childrens?: CardiacProfileNode[];
}

interface CardiacProfileRowOptions {
  treatments: string[];
  surgicalTreatments: string[];
  diseases: string[];
}

@Component({
  selector: 'app-metabolic-calc-cardiac-profile',
  templateUrl: './metabolic-calc-cardiac-profile.component.html',
  styleUrls: ['./metabolic-calc-cardiac-profile.component.scss']
})
export class MetabolicCalcCardiacProfileComponent implements OnInit {
  @Input() data: any;

  _age = 0;

  cardiacProfileForm: FormGroup = new FormGroup({
    pathologies: new FormArray([]),
    complications: new FormControl([])
  });

  cardiacProfile: CardiacProfileNode[] = [];
  pathologies: string[] = [];
  complications: string[] = [];
  rowOptions: CardiacProfileRowOptions[] = [];

  constructor(private formGroupDirective: FormGroupDirective, private metabolicoService: MetabolicoService) {}

  get pathologiesFormArray(): FormArray {
    return this.cardiacProfileForm.get('pathologies') as FormArray;
  }

  get canAddPathology(): boolean {
    if (this.pathologiesFormArray.length === 0) {
      return true;
    }

    return this.pathologiesFormArray.at(this.pathologiesFormArray.length - 1).valid;
  }

  get hasSelectedDisease(): boolean {
    return this.pathologiesFormArray.controls.some((control) => Boolean((control as FormGroup).get('disease')?.value));
  }

  @Input() set age(value: number) {
    this._age = value;
  }

  ngOnInit() {
    if (this.formGroupDirective.form) {
      this.formGroupDirective.form.setControl('cardiacProfile', this.cardiacProfileForm);
    }

    this.metabolicoService.cardiacProfileGet().subscribe({
      next: (cardiacData) => {
        const cardiacProfile = cardiacData as unknown as CardiacProfileNode[];

        this.cardiacProfile = cardiacProfile;
        this.pathologies = this.extractPathologies(cardiacProfile);
        this.complications = this.extractComplications(cardiacProfile);

        if (this.data) {
          this.preloadData(this.data);
        } else if (this.pathologiesFormArray.length === 0) {
          this.appendPathology();
        }
      }
    });
  }

  extractPathologies(cardiacProfile: CardiacProfileNode[]): string[] {
    return cardiacProfile.map(({ key }) => key).filter((key): key is string => Boolean(key) && key !== 'COMPLICATIONS');
  }

  extractComplications(cardiacProfile: CardiacProfileNode[]): string[] {
    return (
      cardiacProfile
        .find(({ key }) => key === 'COMPLICATIONS')
        ?.childrens?.map(({ key }) => key)
        .filter((key): key is string => Boolean(key)) ?? []
    );
  }

  extractNodeKeys(nodes?: CardiacProfileNode[]): string[] {
    return nodes?.map(({ key }) => key).filter((key): key is string => Boolean(key)) ?? [];
  }

  getPathologyTranslate(key: string) {
    return `METABOLIC-CALC.CARDIAC-PROFILE.${key}`;
  }

  addPathology() {
    if (!this.canAddPathology) {
      this.markLastPathologyRowAsTouched();
      return;
    }

    this.appendPathology();
  }

  deletePathology(index: number) {
    this.pathologiesFormArray.removeAt(index);
    this.rowOptions.splice(index, 1);
    if (this.pathologiesFormArray.length === 0) {
      this.appendPathology();
    }
    this.refreshAllRowOptions();
  }

  private appendPathology() {
    const pathologyGroup = this.createPathologyGroup();

    this.pathologiesFormArray.push(pathologyGroup);
    this.rowOptions.push(this.createEmptyRowOptions());
    this.registerRowValueChanges(pathologyGroup);
    this.refreshAllRowOptions();
  }

  private markLastPathologyRowAsTouched() {
    const lastRow = this.pathologiesFormArray.at(this.pathologiesFormArray.length - 1) as FormGroup | null;

    if (!lastRow) {
      return;
    }

    lastRow.markAllAsTouched();
    lastRow.updateValueAndValidity({ emitEvent: false });
  }

  private createPathologyGroup(): FormGroup {
    return new FormGroup({
      pathology: new FormControl(''),
      treatment: new FormControl(''),
      surgicalTreatment: new FormControl(''),
      disease: new FormControl(''),
      ageAtEvent: new FormControl('', { validators: [CalcValidators.ageAtEvent(0, this._age)] })
    });
  }

  private createEmptyRowOptions(): CardiacProfileRowOptions {
    return {
      treatments: [],
      surgicalTreatments: [],
      diseases: []
    };
  }

  private registerRowValueChanges(pathologyGroup: FormGroup) {
    pathologyGroup.get('pathology')?.valueChanges.subscribe((pathology) => {
      this.onPathologyChange(pathologyGroup, pathology);
    });

    pathologyGroup.get('treatment')?.valueChanges.subscribe((treatment) => {
      this.onTreatmentChange(pathologyGroup, treatment);
    });

    pathologyGroup.get('surgicalTreatment')?.valueChanges.subscribe((surgicalTreatment) => {
      this.onSurgicalTreatmentChange(pathologyGroup, surgicalTreatment);
    });

    pathologyGroup.get('disease')?.valueChanges.subscribe((disease) => {
      this.onDiseaseChange(pathologyGroup, disease);
    });
  }

  private onPathologyChange(pathologyGroup: FormGroup, pathology: string) {
    this.resetControls(pathologyGroup, ['treatment', 'surgicalTreatment', 'disease', 'ageAtEvent']);
    this.refreshAllRowOptions();
  }

  private onTreatmentChange(pathologyGroup: FormGroup, treatment: string) {
    this.resetControls(pathologyGroup, ['surgicalTreatment', 'disease', 'ageAtEvent']);
    this.refreshAllRowOptions();
  }

  private onSurgicalTreatmentChange(pathologyGroup: FormGroup, surgicalTreatment: string) {
    this.resetControls(pathologyGroup, ['disease', 'ageAtEvent']);
    this.refreshAllRowOptions();
  }

  private onDiseaseChange(pathologyGroup: FormGroup, disease: string) {
    const rowIndex = this.getRowIndex(pathologyGroup);

    if (rowIndex < 0) {
      return;
    }

    if (disease && this.hasSiblingSelectedDiseaseForSameBranch(rowIndex)) {
      pathologyGroup.get('disease')?.setValue('', { emitEvent: false });
    }

    this.refreshAllRowOptions();
  }

  private resetControls(pathologyGroup: FormGroup, controlNames: string[]) {
    controlNames.forEach((controlName) => {
      pathologyGroup.get(controlName)?.setValue('', { emitEvent: false });
    });
  }

  private setRequiredValidator(control: AbstractControl | null, isRequired: boolean) {
    if (!control) {
      return;
    }

    control.clearValidators();

    if (isRequired) {
      control.addValidators(Validators.required);
    }

    control.updateValueAndValidity({ emitEvent: false });
  }

  private getRowIndex(pathologyGroup: FormGroup): number {
    return this.pathologiesFormArray.controls.indexOf(pathologyGroup);
  }

  private refreshAllRowOptions() {
    this.rowOptions = this.pathologiesFormArray.controls.map((control, rowIndex) => {
      const pathologyGroup = control as FormGroup;
      const rowOptions = this.buildRowOptions(pathologyGroup, rowIndex);

      this.updateControlValidators(pathologyGroup, rowIndex, rowOptions);

      return rowOptions;
    });

    if (!this.hasSelectedDisease) {
      this.cardiacProfileForm.get('complications')?.setValue([], { emitEvent: false });
    }
  }

  private buildRowOptions(pathologyGroup: FormGroup, rowIndex: number): CardiacProfileRowOptions {
    const pathologyNode = this.findNodeByKey(pathologyGroup.get('pathology')?.value, this.cardiacProfile);

    if (!pathologyNode) {
      return this.createEmptyRowOptions();
    }

    const pathologyChildren = pathologyNode.childrens ?? [];
    const pathologyHasNestedChildren = pathologyChildren.some((child) => (child.childrens?.length ?? 0) > 0);

    if (!pathologyHasNestedChildren) {
      return {
        treatments: [],
        surgicalTreatments: [],
        diseases: this.filterAvailableDiseases(pathologyGroup, rowIndex, this.extractNodeKeys(pathologyChildren))
      };
    }

    const treatmentOptions = this.extractNodeKeys(pathologyChildren);
    const treatmentNode = this.findNodeByKey(pathologyGroup.get('treatment')?.value, pathologyChildren);

    if (!treatmentNode) {
      return {
        treatments: treatmentOptions,
        surgicalTreatments: [],
        diseases: []
      };
    }

    const treatmentChildren = treatmentNode.childrens ?? [];
    const treatmentHasNestedChildren = treatmentChildren.some((child) => (child.childrens?.length ?? 0) > 0);

    if (!treatmentHasNestedChildren) {
      return {
        treatments: treatmentOptions,
        surgicalTreatments: [],
        diseases: this.filterAvailableDiseases(pathologyGroup, rowIndex, this.extractNodeKeys(treatmentChildren))
      };
    }

    const surgicalTreatmentOptions = this.extractNodeKeys(treatmentChildren);
    const surgicalTreatmentNode = this.findNodeByKey(pathologyGroup.get('surgicalTreatment')?.value, treatmentChildren);

    return {
      treatments: treatmentOptions,
      surgicalTreatments: surgicalTreatmentOptions,
      diseases: this.filterAvailableDiseases(pathologyGroup, rowIndex, this.extractNodeKeys(surgicalTreatmentNode?.childrens))
    };
  }

  private filterAvailableDiseases(pathologyGroup: FormGroup, rowIndex: number, diseases: string[]): string[] {
    const selectedDisease = pathologyGroup.get('disease')?.value;

    if (selectedDisease) {
      return diseases.filter((disease) => disease === selectedDisease);
    }

    if (this.hasSiblingSelectedDiseaseForSameBranch(rowIndex)) {
      return [];
    }

    return diseases;
  }

  private hasSiblingSelectedDiseaseForSameBranch(rowIndex: number): boolean {
    const currentRow = this.pathologiesFormArray.at(rowIndex) as FormGroup;
    const pathology = currentRow.get('pathology')?.value;
    const treatment = currentRow.get('treatment')?.value;
    const surgicalTreatment = currentRow.get('surgicalTreatment')?.value;

    return this.pathologiesFormArray.controls.some((control, index) => {
      if (index === rowIndex) {
        return false;
      }

      const rowGroup = control as FormGroup;

      return (
        rowGroup.get('pathology')?.value === pathology &&
        rowGroup.get('treatment')?.value === treatment &&
        rowGroup.get('surgicalTreatment')?.value === surgicalTreatment &&
        Boolean(rowGroup.get('disease')?.value)
      );
    });
  }

  private findNodeByKey(key: string, nodes: CardiacProfileNode[]): CardiacProfileNode | undefined {
    return nodes.find((node) => node.key === key);
  }

  private preloadData(data: any) {
    const pathologies: any[] = data.pathologies ?? [];

    pathologies.forEach((rowData) => {
      const pathologyGroup = this.createPathologyGroup();
      this.pathologiesFormArray.push(pathologyGroup);
      this.rowOptions.push(this.createEmptyRowOptions());
      this.registerRowValueChanges(pathologyGroup);
      // Set all values silently to avoid cascading resets from valueChanges
      pathologyGroup.setValue(
        {
          pathology: rowData.pathology ?? '',
          treatment: rowData.treatment ?? '',
          surgicalTreatment: rowData.surgicalTreatment ?? '',
          disease: rowData.disease ?? '',
          ageAtEvent: rowData.ageAtEvent ?? ''
        },
        { emitEvent: false }
      );
    });

    if (pathologies.length === 0) {
      this.appendPathology();
    }

    this.refreshAllRowOptions();

    if (data.complications?.length) {
      this.cardiacProfileForm.get('complications')?.setValue(data.complications, { emitEvent: false });
    }
  }

  private updateControlValidators(pathologyGroup: FormGroup, rowIndex: number, rowOptions?: CardiacProfileRowOptions) {
    const currentRowOptions = rowOptions ?? this.rowOptions[rowIndex] ?? this.createEmptyRowOptions();
    const isDiseaseSectionVisible = currentRowOptions.diseases.length > 0;

    this.setRequiredValidator(pathologyGroup.get('treatment'), currentRowOptions.treatments.length > 0);
    this.setRequiredValidator(pathologyGroup.get('surgicalTreatment'), currentRowOptions.surgicalTreatments.length > 0);
    this.setRequiredValidator(pathologyGroup.get('disease'), isDiseaseSectionVisible);
    this.setAgeAtEventValidators(pathologyGroup.get('ageAtEvent'), isDiseaseSectionVisible);
  }

  private setAgeAtEventValidators(control: AbstractControl | null, isRequired: boolean) {
    if (!control) {
      return;
    }

    control.clearValidators();

    const validators: ValidatorFn[] = [CalcValidators.ageAtEvent(0, this._age)];
    if (isRequired) {
      validators.push(Validators.required);
    }

    control.setValidators(validators);
    control.updateValueAndValidity({ emitEvent: false });
  }
}
